/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable no-nested-ternary */

import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Badge, Box, Chip, Icon } from '@saviynt/design-system';

import { SEE_MORE_ON_REDIRECTION } from '../../constants/steps';
import type { NavigationData } from '../../data-layer/reduxTypes';
import type { StepNavigationProps } from '../../data-layer/types';
import useNavigationHandlers from '../../hooks/useNavigationHandlers';
import getStepClassName from '../../utils/classNameHelpers';

import './LeftSidebar.css';

/**
 * Left sidebar navigation component - presentation only
 */
function LeftSidebar({
  scrollToStep,
  navigation,
  isLoading,
  isError,
}: Pick<StepNavigationProps, 'scrollToStep'> & {
  navigation: NavigationData;
  isLoading: boolean;
  isError: boolean;
}) {
  const {
    activeStep,
    skippedSteps,
    completedSteps,
    visitedSteps,
    nameToLabelMap,
  } = navigation;

  const isEditMode = useSelector(
    (state: { copilot: { ui: { mode: string } } }) =>
      state.copilot.ui.mode === 'edit'
  );

  const endpointKey = useSelector(
    (state: {
      copilot: {
        ui: { endpointKey?: string };
        endpointKey?: string;
      };
    }) => state.copilot.ui.endpointKey ?? state.copilot.endpointKey
  );

  const handleSeeAdditionalClick = () => {
    setTimeout(() => {
      window.open(
        `${SEE_MORE_ON_REDIRECTION}${String(endpointKey || '')}`,
        '_blank'
      );
    }, 100);
  };

  const { handleBackClick, handleKeyDown, handleStepClick } =
    useNavigationHandlers({
      scrollToStep,
      visitedSteps,
    });

  const activeStepRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (activeStepRef.current) {
      activeStepRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [activeStep]);

  if (isLoading) {
    return (
      <Box className='Copilot-Sidebar'>
        <Box className='Copilot-NavContent'>
          <div className='Copilot-Nav'>
            <Box className='Copilot-NavItem'>
              <div className='step-container'>
                <div className='step-content'>
                  <Box className='Example-Badge-flexbox'>
                    <Badge size='medium' />
                  </Box>
                  <span className='step-label'>Determining app type...</span>
                </div>
              </div>
            </Box>
          </div>
        </Box>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box className='Copilot-Sidebar'>
        <Box className='Copilot-NavContent'>
          <div className='Copilot-Nav'>
            <Box className='Copilot-NavItem'>
              <div className='step-container error'>
                <div className='step-content'>
                  <span className='step-label'>Error loading steps</span>
                </div>
              </div>
            </Box>
          </div>
        </Box>
      </Box>
    );
  }

  return (
    <Box className='Copilot-Sidebar'>
      <Box className='Copilot-NavContent'>
        <div className='Copilot-Nav'>
          <Box className='Copilot-NavItem'>
            <div
              className='Copilot-NavIcon'
              onClick={handleBackClick}
              onKeyDown={handleKeyDown}
              tabIndex={0}
              role='button'>
              <Icon
                kind='ArrowLeft'
                color='--color-foreground-neutral-bold'
                size='small'
              />
              <div className='Copilot-Back-border' />
              <div className='Copilot-Back'> Checkpoint</div>
            </div>

            <div className='Copilot-StepNavigation'>
              {Object.entries(nameToLabelMap).map(([stepId, label]) => {
                const isCompleted = completedSteps.includes(stepId);
                const isVisited = visitedSteps.includes(stepId);
                const isSkipped = skippedSteps.includes(stepId);
                const isActive = activeStep === stepId;
                const isClickable = isVisited;
                const isDisabled = !isClickable;

                if (isSkipped) {
                  return (
                    <div
                      key={stepId}
                      onClick={() => isClickable && handleStepClick(stepId)}
                      onKeyDown={
                        (e) =>
                          e.key === 'Enter' &&
                          isClickable &&
                          handleStepClick(stepId)
                        // eslint-disable-next-line react/jsx-curly-newline
                      }
                      role='button'
                      tabIndex={isClickable ? 0 : -1}
                      className={getStepClassName({
                        isActive,
                        isDisabled,
                        isCompleted,
                      })}>
                      <div className='step-content skipped'>
                        <Box className='Example-Badge-flexbox'>
                          <Icon
                            kind='AlertInfoFilledSeparateColors'
                            size='medium'
                            color='--color-foreground-neutral-disabled'
                          />
                        </Box>
                        <Box className='step-content-box'>
                          <div className='step-label'>{label}</div>
                          <Chip
                            kind='outlined'
                            label='Setup'
                            size='xxSmall'
                            leftIcon={<Icon kind='AlertWarningOutline' />}
                            color='warning'
                          />
                        </Box>
                      </div>
                    </div>
                  );
                }

                return (
                  <div
                    key={stepId}
                    ref={isActive ? activeStepRef : null}
                    onClick={() => isClickable && handleStepClick(stepId)}
                    onKeyDown={
                      (e) =>
                        e.key === 'Enter' &&
                        isClickable &&
                        handleStepClick(stepId)
                      // eslint-disable-next-line react/jsx-curly-newline
                    }
                    role='button'
                    tabIndex={isClickable ? 0 : -1}
                    className={getStepClassName({
                      isActive,
                      isDisabled,
                      isCompleted,
                    })}>
                    <div className='step-content'>
                      <Box className='Example-Badge-flexbox'>
                        {isCompleted ? (
                          <Icon
                            kind='CheckCircleFilledSeparateColors'
                            color='--color-foreground-success-medium'
                            size='medium'
                          />
                        ) : (
                          <Icon
                            kind='StatusToDo'
                            size='medium'
                            color='--color-warning-500'
                          />
                        )}
                      </Box>
                      <Box className='step-content-box'>
                        <span className='step-label'>{label}</span>
                      </Box>
                    </div>
                  </div>
                );
              })}
              {isEditMode && Object.keys(nameToLabelMap).length > 0 && (
                <>
                  <hr className='Copilot-StepDivider' />
                  <div
                    className='Copilot-AdditionalConfig'
                    role='button'
                    tabIndex={0}
                    onClick={handleSeeAdditionalClick}>
                    See Additional Configurations
                  </div>
                </>
              )}
            </div>
          </Box>
        </div>
      </Box>
    </Box>
  );
}

export default LeftSidebar;
